import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Loader2, TrendingUp, TrendingDown, Minus, Radio,
  Eye, ExternalLink, Trophy, Target, Zap, BarChart3,
} from 'lucide-react';
import { apiGet } from '../data/api';
import t from '../i18n/it';

const mono = { fontFamily: 'JetBrains Mono, monospace' };
const heading = { fontFamily: 'Outfit, sans-serif' };
const card = { background: '#18181B', border: '1px solid #27272A' };
const divider = { borderBottom: '1px solid #27272A' };
const rowBorder = { borderBottom: '1px solid #1F1F22' };

function SportBadge({ sport }) {
  return sport === 'tennis' ? (
    <span className="px-1.5 py-0.5 text-xs rounded bg-blue-500/15 text-blue-400">{t.common.tennis}</span>
  ) : (
    <span className="px-1.5 py-0.5 text-xs rounded bg-purple-500/15 text-purple-400">{t.common.table_tennis}</span>
  );
}

function MatchStatusBadge({ status }) {
  if (status === 'live') return <span className="flex items-center gap-1 text-xs text-red-400"><span className="w-1.5 h-1.5 rounded-full bg-red-500 live-dot" />{t.status.live}</span>;
  if (status === 'scheduled') return <span className="px-1.5 py-0.5 text-xs rounded bg-amber-500/15 text-amber-400">{t.status.scheduled}</span>;
  return <span className="px-1.5 py-0.5 text-xs rounded bg-zinc-700/40 text-zinc-500">{t.status.finished}</span>;
}

function SignalBadge({ status }) {
  const m = { active: 'bg-emerald-500/15 text-emerald-400', won: 'bg-blue-500/15 text-blue-400', lost: 'bg-red-500/15 text-red-400' };
  const l = { active: t.status.active, won: t.status.won, lost: t.status.lost };
  return <span className={`px-1.5 py-0.5 text-xs rounded ${m[status] || 'bg-zinc-500/15 text-zinc-400'}`}>{l[status] || status}</span>;
}

function RatingBar({ value, max, label, color }) {
  const pct = value != null ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-xs text-zinc-400 mb-1.5">
        <span>{label}</span>
        <span style={mono}>{value?.toFixed(0) ?? '—'}</span>
      </div>
      <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
      </div>
    </div>
  );
}

function PerfCard({ icon: Icon, iconColor, bgColor, label, mainVal, mainColor, sub }) {
  return (
    <div className="rounded-sm p-4" style={{ background: '#0D0D0F', border: '1px solid #27272A' }} data-testid={`perf-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-5 h-5 rounded flex items-center justify-center ${bgColor}`}><Icon size={10} className={iconColor} /></div>
        <span className="text-xs uppercase tracking-[0.08em] text-zinc-500">{label}</span>
      </div>
      <div className={`text-xl font-bold ${mainColor}`} style={mono}>{mainVal}</div>
      {sub && <div className="text-xs text-zinc-600 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function PlayerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiGet(`/players/${id}/detail`)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex items-center gap-2 justify-center py-16 text-zinc-500"><Loader2 size={18} className="animate-spin" /><span>{t.common.loading}</span></div>;
  if (error) return <div className="text-red-400 text-center py-10">{t.common.error}: {error}</div>;
  if (!data) return null;

  const { player, matches, prematch_signals, live_signals, performance } = data;
  const formColor = player.recent_form_score >= 80 ? '#10B981' : player.recent_form_score >= 60 ? '#F59E0B' : '#EF4444';
  const winPct = performance.total_matches > 0 ? ((performance.wins / performance.total_matches) * 100).toFixed(0) : '—';

  return (
    <div className="space-y-5 fade-in">
      <Link to="/giocatori" data-testid="back-to-players" className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-300 transition-colors">
        <ArrowLeft size={15} /> {t.common.back}
      </Link>

      {/* ====== 1. PLAYER HEADER ====== */}
      <div className="rounded-sm p-6" style={card} data-testid="player-header">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <SportBadge sport={player.sport} />
              <span className="text-xs text-zinc-500" style={mono}>{t.players.rating} {player.current_rating.toFixed(0)}</span>
              <span className="text-xs text-zinc-600" style={mono}>{t.players.form_score} {player.recent_form_score.toFixed(0)}</span>
            </div>
            <h1 className="text-4xl font-bold text-zinc-50 tracking-tight" style={heading}>{player.name}</h1>
            <p className="text-zinc-400 mt-1 flex items-center gap-3">
              <span>{player.country}</span>
              <span className="text-zinc-600">|</span>
              <span className="text-xs text-zinc-500">{t.players.handedness}: {player.handedness === 'right' ? t.common.right : t.common.left}</span>
            </p>
          </div>
          <div className="flex items-center gap-3">
            {performance.total_matches > 0 && (
              <div className="text-right">
                <div className="text-xs text-zinc-600">{t.player_detail.record}</div>
                <div className="text-lg font-bold text-zinc-200" style={mono}>{performance.wins}V-{performance.losses}P</div>
                <div className="text-xs text-zinc-500">{winPct}% {t.player_detail.win_rate}</div>
              </div>
            )}
          </div>
        </div>
        <div className="mt-5 pt-4" style={{ borderTop: '1px solid #27272A' }}>
          <div className="text-xs uppercase tracking-[0.08em] text-zinc-500 mb-2.5">{t.players.form_score}</div>
          <div className="flex items-center gap-3">
            <div className="flex-1 h-3 rounded-full bg-zinc-800 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${player.recent_form_score}%`, background: formColor }} />
            </div>
            <span className="text-lg font-bold text-zinc-200" style={mono}>{player.recent_form_score.toFixed(0)}</span>
          </div>
        </div>
      </div>

      {/* ====== 2. RATINGS ====== */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ratings-section">
        <div className="rounded-sm p-4" style={{ background: '#0D0D0F', border: '1px solid #27272A' }}>
          <div className="text-xs uppercase tracking-[0.08em] text-zinc-500 mb-2">{t.player_detail.current_rating}</div>
          <div className="text-2xl font-bold text-emerald-400" style={mono}>{player.current_rating.toFixed(0)}</div>
          <div className="text-xs text-zinc-600 mt-1">{t.player_detail.elo_score}</div>
        </div>
        <div className="rounded-sm p-4" style={{ background: '#0D0D0F', border: '1px solid #27272A' }}>
          <div className="text-xs uppercase tracking-[0.08em] text-zinc-500 mb-2">{t.player_detail.recent_form}</div>
          <div className="text-2xl font-bold" style={{ ...mono, color: formColor }}>{player.recent_form_score.toFixed(0)}</div>
          <div className="text-xs text-zinc-600 mt-1">{t.player_detail.latest_results}</div>
        </div>
        <div className="rounded-sm p-4" style={{ background: '#0D0D0F', border: '1px solid #27272A' }}>
          <div className="text-xs uppercase tracking-[0.08em] text-zinc-500 mb-2">{t.players.country}</div>
          <div className="text-2xl font-bold text-zinc-100" style={mono}>{player.country}</div>
        </div>
        <div className="rounded-sm p-4" style={{ background: '#0D0D0F', border: '1px solid #27272A' }}>
          <div className="text-xs uppercase tracking-[0.08em] text-zinc-500 mb-2">{t.player_detail.dominant_hand}</div>
          <div className="text-2xl font-bold text-zinc-100" style={mono}>{player.handedness === 'right' ? t.common.right : t.common.left}</div>
        </div>
      </div>

      {player.sport === 'tennis' && (
        <div className="rounded-sm p-5" style={card} data-testid="surface-ratings">
          <div className="text-xs uppercase tracking-[0.08em] text-zinc-500 mb-4">{t.players.surface_ratings}</div>
          <div className="space-y-4">
            <RatingBar label={t.players.surface_hard} value={player.surface_rating_hard} max={100} color="#3B82F6" />
            <RatingBar label={t.players.surface_clay} value={player.surface_rating_clay} max={100} color="#F97316" />
            <RatingBar label={t.players.surface_grass} value={player.surface_rating_grass} max={100} color="#22C55E" />
          </div>
        </div>
      )}

      {/* ====== 6. PERFORMANCE WIDGETS ====== */}
      <div data-testid="performance-widgets">
        <h2 className="text-xs font-medium uppercase tracking-[0.1em] text-zinc-500 mb-3 px-1">{t.player_detail.performance}</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <PerfCard
            icon={Trophy} iconColor="text-emerald-400" bgColor="bg-emerald-500/10"
            label={t.player_detail.wins}
            mainVal={performance.total_matches > 0 ? `${performance.wins}/${performance.total_matches}` : '—'}
            mainColor="text-emerald-400"
            sub={performance.total_matches > 0 ? `${winPct}% ${t.player_detail.win_rate_label}` : t.player_detail.no_finished_match}
          />
          <PerfCard
            icon={Target} iconColor="text-blue-400" bgColor="bg-blue-500/10"
            label={t.player_detail.avg_confidence}
            mainVal={performance.avg_confidence != null ? `${(performance.avg_confidence * 100).toFixed(0)}%` : '—'}
            mainColor="text-blue-400"
            sub={performance.prematch_signal_count > 0 ? `${t.common.on} ${performance.prematch_signal_count} segnali prematch` : null}
          />
          <PerfCard
            icon={Zap} iconColor="text-amber-400" bgColor="bg-amber-500/10"
            label={t.player_detail.avg_ev_prematch}
            mainVal={performance.avg_ev_prematch != null ? `+${(performance.avg_ev_prematch * 100).toFixed(1)}%` : '—'}
            mainColor="text-amber-400"
            sub={t.player_detail.ev_prematch_sub}
          />
          <PerfCard
            icon={BarChart3} iconColor="text-purple-400" bgColor="bg-purple-500/10"
            label={t.signals.momentum}
            mainVal={performance.avg_momentum != null ? `${performance.avg_momentum > 0 ? '+' : ''}${performance.avg_momentum.toFixed(0)}` : '—'}
            mainColor={performance.avg_momentum > 0 ? 'text-emerald-400' : performance.avg_momentum < 0 ? 'text-red-400' : 'text-zinc-400'}
            sub={performance.live_signal_count > 0 ? `media ${t.common.on} ${performance.live_signal_count} segnali live` : null}
          />
        </div>
      </div>

      {/* ====== 3. RECENT MATCHES ====== */}
      {matches.length > 0 && (
        <div className="rounded-sm" style={card} data-testid="recent-matches-section">
          <div className="px-5 py-4 flex items-center gap-2" style={divider}>
            <Trophy size={15} className="text-zinc-400" />
            <h2 className="font-semibold text-zinc-200 text-sm" style={heading}>{t.players.recent_matches}</h2>
            <span className="ml-auto text-xs text-zinc-600">{matches.length}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={divider}>
                  {[t.player_detail.th_opponent, t.player_detail.th_tournament, t.player_detail.th_sport, t.player_detail.th_date, t.player_detail.th_status, t.player_detail.th_result, ''].map((h, i) => (
                    <th key={i} className={`py-2.5 px-4 text-xs uppercase tracking-[0.06em] text-zinc-600 font-medium whitespace-nowrap ${i === 0 ? 'text-left' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {matches.map((m) => {
                  const isP1 = m.player1_id === player.id;
                  const opponent = isP1 ? m.player2_name : m.player1_name;
                  const won = m.winner_id === player.id;
                  const lost = m.status === 'finished' && m.winner_id && m.winner_id !== player.id;
                  return (
                    <tr key={m.id} data-testid={`match-row-${m.id}`} style={rowBorder} className="transition-colors hover:bg-zinc-800/30">
                      <td className="px-4 py-2.5 text-zinc-200 font-medium">{opponent}</td>
                      <td className="px-4 py-2.5 text-zinc-400 text-xs whitespace-nowrap">{m.tournament_name}</td>
                      <td className="px-4 py-2.5"><SportBadge sport={m.sport} /></td>
                      <td className="px-4 py-2.5 text-zinc-500 whitespace-nowrap" style={{ ...mono, fontSize: 11 }}>
                        {new Date(m.start_time).toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="px-4 py-2.5"><MatchStatusBadge status={m.status} /></td>
                      <td className="px-4 py-2.5">
                        {won && <span className="px-1.5 py-0.5 text-xs rounded bg-emerald-500/15 text-emerald-400">{t.player_detail.won}</span>}
                        {lost && <span className="px-1.5 py-0.5 text-xs rounded bg-red-500/15 text-red-400">{t.player_detail.lost}</span>}
                        {!won && !lost && m.status !== 'finished' && <span className="text-zinc-600 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-2.5">
                        <Link to={`/incontri/${m.id}`} className="text-zinc-600 hover:text-emerald-400 transition-colors"><ExternalLink size={13} /></Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ====== 4. PREMATCH SIGNALS ====== */}
      {prematch_signals.length > 0 && (
        <div className="rounded-sm" style={card} data-testid="prematch-signals-section">
          <div className="px-5 py-4 flex items-center gap-2" style={divider}>
            <TrendingUp size={15} className="text-emerald-400" />
            <h2 className="font-semibold text-zinc-200 text-sm" style={heading}>{t.player_detail.prematch_signals}</h2>
            <span className="ml-auto text-xs text-zinc-600">{prematch_signals.length}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={divider}>
                  {[t.signals.match, t.signals.selection, t.signals.bookmaker, t.signals.odds_bk, t.signals.odds_fair, t.signals.ev_pct, t.signals.confidence, t.signals.watch_live, t.signals.status].map((h, i) => (
                    <th key={i} className={`py-2.5 px-4 text-xs uppercase tracking-[0.06em] text-zinc-600 font-medium whitespace-nowrap ${i >= 3 && i <= 6 ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {prematch_signals.map((sig) => (
                  <tr key={sig.id} data-testid={`pm-signal-${sig.id}`} style={rowBorder} className="transition-colors hover:bg-zinc-800/30">
                    <td className="px-4 py-2.5 text-zinc-200 text-xs font-medium whitespace-nowrap">{sig.match_name}</td>
                    <td className="px-4 py-2.5 text-zinc-300 text-xs font-medium">{sig.selection_name}</td>
                    <td className="px-4 py-2.5 text-zinc-500 text-xs">{sig.bookmaker_name}</td>
                    <td className="px-4 py-2.5 text-right text-emerald-400" style={{ ...mono, fontSize: 12 }}>{sig.bookmaker_odds?.toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right text-zinc-500" style={{ ...mono, fontSize: 11 }}>{sig.fair_odds?.toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right text-emerald-400" style={{ ...mono, fontSize: 12 }}>+{(sig.expected_value * 100).toFixed(1)}%</td>
                    <td className="px-4 py-2.5 text-right text-zinc-400" style={{ ...mono, fontSize: 11 }}>{(sig.confidence_score * 100).toFixed(0)}%</td>
                    <td className="px-4 py-2.5 text-center">{sig.watch_live_flag ? <Eye size={12} className="text-amber-400 inline" /> : <span className="text-zinc-700">—</span>}</td>
                    <td className="px-4 py-2.5"><SignalBadge status={sig.signal_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ====== 5. LIVE SIGNALS ====== */}
      {live_signals.length > 0 && (
        <div className="rounded-sm" style={card} data-testid="live-signals-section">
          <div className="px-5 py-4 flex items-center gap-2" style={divider}>
            <Radio size={15} className="text-red-400" />
            <h2 className="font-semibold text-zinc-200 text-sm" style={heading}>{t.player_detail.live_signals}</h2>
            <span className="ml-auto text-xs text-zinc-600">{live_signals.length}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr style={divider}>
                  {[t.signals.match, t.signals.selection, t.signals.odds_live, t.signals.odds_fair, t.signals.ev_pct, t.signals.momentum, t.signals.pressure, t.signals.status].map((h, i) => (
                    <th key={i} className={`py-2.5 px-4 text-xs uppercase tracking-[0.06em] text-zinc-600 font-medium whitespace-nowrap ${i >= 2 && i <= 4 ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {live_signals.map((sig) => (
                  <tr key={sig.id} data-testid={`live-signal-${sig.id}`} style={rowBorder} className="transition-colors hover:bg-zinc-800/30">
                    <td className="px-4 py-2.5 text-zinc-200 text-xs font-medium whitespace-nowrap">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500 live-dot inline-block mr-1.5" />
                      {sig.match_name}
                    </td>
                    <td className="px-4 py-2.5 text-zinc-300 text-xs font-medium">{sig.selection_name}</td>
                    <td className="px-4 py-2.5 text-right text-emerald-400" style={{ ...mono, fontSize: 12 }}>{sig.live_odds?.toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right text-zinc-500" style={{ ...mono, fontSize: 11 }}>{sig.fair_live_odds?.toFixed(2)}</td>
                    <td className="px-4 py-2.5 text-right text-emerald-400" style={{ ...mono, fontSize: 12 }}>+{(sig.expected_value * 100).toFixed(1)}%</td>
                    <td className="px-4 py-2.5">
                      {sig.momentum_score > 20 ? (
                        <span className="text-xs text-emerald-400 flex items-center gap-0.5"><TrendingUp size={11} /> +{sig.momentum_score.toFixed(0)}</span>
                      ) : sig.momentum_score < -20 ? (
                        <span className="text-xs text-red-400 flex items-center gap-0.5"><TrendingDown size={11} /> {sig.momentum_score.toFixed(0)}</span>
                      ) : (
                        <span className="text-xs text-zinc-500 flex items-center gap-0.5"><Minus size={11} /> {sig.momentum_score.toFixed(0)}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs ${sig.pressure_score >= 70 ? 'text-red-400' : sig.pressure_score >= 50 ? 'text-amber-400' : 'text-zinc-500'}`} style={mono}>{sig.pressure_score.toFixed(0)}</span>
                    </td>
                    <td className="px-4 py-2.5"><SignalBadge status={sig.signal_status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
